import React, { Fragment, useState, useEffect } from 'react';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';
import { Button, ButtonToolbar, Card, CardBody, Col, Row } from 'reactstrap';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Field, reduxForm, formValueSelector, reset } from 'redux-form';
import {
  MATERIALS_SUCCESS,
  getCoursesList as getMaterialsListAction,
  materialClearState as materialClearStateAction,
  materialClearFlashMessage as materialClearFlashMessageAction,
} from '../../../../redux/actions/materialsAction';
import renderField from '../../../../shared/components/form/TextField';
import DataTable from '../../../../shared/components/data-table';
import { COMPLETED, WAIT } from '../../../../shared/components/data-table/submitStatuses';
import { DATA, ACTION } from '../../../../shared/components/data-table/columnTypes';
import { LINK } from '../../../../shared/components/data-table/buttonTypes';
import { FullWideNotification } from '../../../../shared/components/Notification';
import store from '../../../App/store';

const MaterialTable = ({
  getMaterialsList,
  materialClearState,
  materialClearFlashMessage,
  showNotification,
  materialsList,
  pagination,
  loadStatus,
  flashMessage,
  resetForm,
}) => {
  const [data, setData] = useState({
    columns: [],
    buttons: [],
    isFind: false,
    status: WAIT,
  });

  const clearFilters = () => {
    resetForm(MaterialTable.FORM_NAME);
    setData({ ...data, isFind: true });
  };

  const getConditions = () => {
    const selector = formValueSelector(MaterialTable.FORM_NAME);
    return selector(store.getState(), 'header', 'typeName', 'duration');
  };

  const handleFind = () => {
    setData({ ...data, isFind: true });
  };

  const show = (color, message) =>
    showNotification({
      notification: <FullWideNotification color={color} message={message} />,
      position: 'full',
    });

  useEffect(() => {
    if (loadStatus === MATERIALS_SUCCESS) {
      setData({ ...data, isFind: false });
    }
  }, [loadStatus]);

  useEffect(() => {
    if (flashMessage && flashMessage.length) {
      setTimeout(() => {
        show('success', flashMessage);
        materialClearFlashMessage();
      }, 1000);
    }
  }, [flashMessage]);

  useEffect(() => {
    const columns = [
      {
        label: '#',
        dbColumn: 'createdAt',
        listColumn: 'number',
        sortable: true,
        width: 5,
        type: DATA,
      },
      {
        label: 'Название материала',
        dbColumn: 'header',
        listColumn: 'header',
        sortable: true,
        width: 40,
        type: DATA,
      },
      {
        label: 'Тип материала',
        dbColumn: 'typeName',
        listColumn: 'typeName',
        sortable: true,
        width: 20,
        type: DATA,
      },
      {
        label: 'Продолжительность',
        dbColumn: 'duration',
        listColumn: 'duration',
        sortable: true,
        width: 15,
        type: DATA,
      },
      {
        label: 'Создан',
        dbColumn: 'createdAt',
        listColumn: 'createdAt',
        sortable: true,
        width: 15,
        type: DATA,
      },
      {
        label: 'Действия',
        dbColumn: '_id',
        listColumn: 'id',
        sortable: false,
        width: 5,
        type: ACTION,
      },
    ];

    const buttons = [
      {
        name: 'edit',
        color: 'primary',
        icon: 'edit',
        type: LINK,
        link: '/materials/',
        text: 'Редактировать',
      },
    ];

    setData({
      ...data,
      columns,
      buttons,
      status: COMPLETED,
    });

    return () => {
      materialClearState();
    };
  }, []);

  return (
    <Fragment>
      {data.status === COMPLETED && (
        <Fragment>
          <Row>
            <Col md={12} lg={12}>
              <Card>
                <CardBody>
                  <div className="card__title">
                    <h5 className="bold-text">Поиск по материалам курсов</h5>
                  </div>
                  <form className="form gap-20">
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Название материала</span>
                        <div className="form__form-group-field">
                          <Field name="header" component={renderField} type="text" placeholder="Название материала" />
                        </div>
                      </div>
                    </Col>
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Тип материала</span>
                        <div className="form__form-group-field">
                          <Field name="typeName" component={renderField} type="text" placeholder="Тип материала" />
                        </div>
                      </div>
                    </Col>
                    <Col className="p-0">
                      <div className="form__form-group">
                        <span className="form__form-group-label">Продолжительность</span>
                        <div className="form__form-group-field">
                          <Field name="duration" component={renderField} type="text" placeholder="Продолжительность" />
                        </div>
                      </div>
                    </Col>
                  </form>
                  <ButtonToolbar className="form__button-toolbar wizard__toolbar">
                    <Button color="primary" type="button" onClick={handleFind}>
                      Найти
                    </Button>
                    <Button color="secondary" type="button" onClick={clearFilters}>
                      Очистить
                    </Button>
                  </ButtonToolbar>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md={12} lg={12}>
              <DataTable
                addButton={{ to: '/materials/create', caption: 'Добавить материал' }}
                columns={data.columns}
                buttons={data.buttons}
                getConditions={getConditions}
                getList={getMaterialsList}
                list={materialsList}
                pagination={pagination}
                isFind={data.isFind}
                loadListStatus={loadStatus}
                declensions={['материал', 'материала', 'материалов']}
              />
            </Col>
          </Row>
        </Fragment>
      )}
    </Fragment>
  );
};

MaterialTable.FORM_NAME = 'materials_table';

MaterialTable.propTypes = {
  getMaterialsList: PropTypes.func.isRequired,
  materialClearState: PropTypes.func.isRequired,
  materialClearFlashMessage: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  materialsList: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  // eslint-disable-next-line react/require-default-props
  pagination: PropTypes.objectOf(PropTypes.any),
  loadStatus: PropTypes.string.isRequired,
  flashMessage: PropTypes.string.isRequired,
  resetForm: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  materialsList: state.materials.list,
  pagination: state.materials.pagination,
  loadStatus: state.materials.loadListStatus,
  flashMessage: state.materials.flash,
});

const mapDispatchToProps = (dispatch) => ({
  getMaterialsList: (params) => dispatch(getMaterialsListAction(params)),
  materialClearState: () => dispatch(materialClearStateAction()),
  materialClearFlashMessage: () => dispatch(materialClearFlashMessageAction()),
  resetForm: (formName) => dispatch(reset(formName)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps)(withTranslation('common')(withRouter(MaterialTable)));

export default reduxForm({
  form: MaterialTable.FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(withConnect);
